import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { FileClock, Plus } from "lucide-react";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { STATUS_LABELS, statusPillClass, isKnownImportStatus } from "../../lib/importStatus";

type ImportJobSummary = {
  id: string;
  status: string;
  entity_type: string;
  scope_type?: string;
  created_at?: string;
  created_by_user_id?: string;
  file?: { filename?: string; byte_size?: number };
  counts?: Record<string, number>;
};

export default function ImportsIndex() {
  const navigate = useNavigate();
  const role = useEffectiveRole();
  const canCreate = canAtLeast(role, "manager_trainer");
  const [jobs, setJobs] = useState<ImportJobSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<ImportJobSummary[]>("/import/jobs");
        if (!cancelled) setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setJobs([]);
          toastError(e, "Could not load import jobs.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <PageHeader
        title="Imports"
        subtitle="Contact CSV imports · durable worker · resumable"
        actions={
          canCreate ? (
            <Button
              className="rounded-full"
              onClick={() => navigate("/imports/new")}
              data-testid="new-import-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New import
            </Button>
          ) : null
        }
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-4">
        {jobs === null ? (
          <div className="text-sm text-slate-500" data-testid="imports-loading">
            Loading imports…
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={FileClock}
            title="No import jobs yet"
            description={canCreate ? "Start your first Contact CSV import." : "Ask a manager to start an import to see it here."}
            action={
              canCreate ? (
                <Button
                  className="rounded-full"
                  onClick={() => navigate("/imports/new")}
                  data-testid="empty-new-import-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> New import
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="imports-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">ID</th>
                  <th className="text-left px-4 py-2.5 font-medium">Entity</th>
                  <th className="text-left px-4 py-2.5 font-medium">Scope</th>
                  <th className="text-left px-4 py-2.5 font-medium">File</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr
                    key={j.id}
                    className="border-t border-slate-200 hover:bg-slate-50/60 cursor-pointer"
                    data-testid={`import-row-${j.id.slice(0, 8)}`}
                    onClick={() => navigate(`/imports/${j.id}`)}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                      {j.id.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2.5">{j.entity_type}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">
                      {j.scope_type || "tenant"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">
                      {j.file?.filename || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusPillClass(j.status)}`}
                        data-testid={`import-status-${j.id.slice(0, 8)}`}
                      >
                        {isKnownImportStatus(j.status) ? STATUS_LABELS[j.status] : j.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">
                      {j.created_at ? new Date(j.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
